/*
 * The browser's reads of the API, and what a failed one means (frontend performance plan, W3 and
 * W4). A screen decides what to keep on a failure by its kind, never by its text:
 * - `signedOut` (401): the session is gone; nothing private stays on screen.
 * - `refused` (403): this person may no longer see it; cached content must not stay either.
 * - `transient` (the network, 429, 5xx): the server could not answer now; what was shown may stay,
 *   marked as not refreshed, with a way to try again.
 * Anything else (400, 404, 409, 422) is the request's own answer.
 *
 * A server failure names a `reference` (`app.ts`, `onError`), kept here so the screen can quote it.
 * Reads pass TanStack Query's `signal`, so a read nobody waits for any more is aborted.
 */
import { z } from "zod";

const FailureBodySchema = z
  .object({ error: z.string().optional(), reference: z.string().optional() })
  .loose();

export class ApiError extends Error {
  readonly code: string | null;
  readonly reference: string | null;
  constructor(
    readonly status: number,
    /** The refusal's whole body, unparsed, for a caller that knows its shape. */
    readonly body: unknown = null,
    /** How long the server asked to wait before trying again (`Retry-After`), if it said. */
    readonly retryAfterMs: number | null = null,
  ) {
    const failure = FailureBodySchema.safeParse(body).data;
    super(failure?.error ?? (status === 0 ? "network" : `http_${status}`));
    this.name = "ApiError";
    this.code = failure?.error ?? null;
    this.reference = failure?.reference ?? null;
  }
  get signedOut(): boolean {
    return this.status === 401;
  }
  get refused(): boolean {
    return this.status === 403;
  }
  /** The workspace is not one this person may open: foreign, missing, or offboarded since. */
  get workspaceGone(): boolean {
    return this.status === 404 && this.code === "workspace_not_found";
  }
  get transient(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

/** The server's reference for a failure, for the screen to quote; null when it gave none. */
export function failureReferenceOf(error: unknown): string | null {
  return error instanceof ApiError ? error.reference : null;
}

/** An aborted read is the caller giving up, not the server failing: it is passed on as it is. */
function aborted(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/** A GET of `path` as JSON, or an `ApiError` saying how it failed. */
export async function getJson(
  path: string,
  signal?: AbortSignal,
  headers?: HeadersInit,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: "same-origin",
      ...(signal ? { signal } : {}),
      ...(headers ? { headers } : {}),
    });
  } catch (error) {
    if (aborted(error)) throw error;
    throw new ApiError(0);
  }
  if (!response.ok)
    throw new ApiError(
      response.status,
      await response.json().catch(() => null),
      retryAfter(response.headers.get("retry-after")),
    );
  return response.json();
}

/** `Retry-After` in seconds, as milliseconds; a date or nonsense is ignored. */
function retryAfter(header: string | null): number | null {
  const seconds = header === null ? Number.NaN : Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

/**
 * At most one more try for a read that failed transiently (W4): the server or the network did not
 * answer now. A refusal, a validation failure or a missing thing is the server's answer and is not
 * asked again; neither is anything while the browser is offline, where reads pause on their own.
 */
export function retryRead(failures: number, error: unknown): boolean {
  return failures < 1 && error instanceof ApiError && error.transient;
}

/** The wait before that try: what the server asked, else a jittered 0.3 to 0.8 s. */
export function retryReadDelay(_attempt: number, error: unknown, random = Math.random): number {
  if (error instanceof ApiError && error.retryAfterMs !== null)
    return Math.min(error.retryAfterMs, 10_000);
  return 300 + Math.round(random() * 500);
}
